import React from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {RiArrowDownSLine, RiFileTextLine} from "react-icons/ri";
import {initials, avatarColorFor} from "../../helpers/avatar";
import {formatHM} from "../../helpers/time";
import MessageMenu from "../MessageMenu";
import ImageLightbox from "../ImageLightbox";
import {Row, Avatar, Bubble, Sender, Content, Time, MenuTrigger, MediaImg, MediaSticker, MediaVideo, DocCard, MediaUnavailable, ReactionRow, ReactionChip} from "./styles";

/**
 * Balão de uma mensagem do grupo. from_me=true -> direita (azul, sem remetente);
 * from_me=false -> esquerda (avatar + nome do remetente). Renderiza mídia por tipo
 * (image/video/audio/document) acima da caption; texto puro segue simples.
 */
const GroupMessageBubble = ({message, onPin}) => {
    const {t} = useTranslation();
    const {id, from, from_me: fromMe, content, type, media_url: mediaURL, media_name: mediaName, sent_at: sentAt} = message;
    const senderLabel = from?.name || from?.jid || "";
    const triggerRef = React.useRef(null);
    const [menuRect, setMenuRect] = React.useState(null);
    const [lightbox, setLightbox] = React.useState(false);

    const src = mediaURL ? (process.env.REACT_APP_LINK_API || "") + mediaURL : null;

    const openMenu = () => {
        if (triggerRef.current) setMenuRect(triggerRef.current.getBoundingClientRect());
    };
    const copy = () => {
        try {
            navigator.clipboard?.writeText?.(content);
        } catch (e) {
            /* clipboard indisponível -> ignora */
        }
    };
    const menu = menuRect && (
        <MessageMenu
            anchorRect={menuRect}
            onClose={() => setMenuRect(null)}
            onPin={() => onPin?.(id)}
            onCopy={copy}
        />
    );

    const media = () => {
        if (!type || type === "text") return null;
        if (!src) return <MediaUnavailable>{t("words.media_unavailable")}</MediaUnavailable>;
        if (type === "sticker") return <MediaSticker src={src} alt="sticker"/>;
        if (type === "image") return <MediaImg src={src} alt={content || "image"} onClick={() => setLightbox(true)}/>;
        if (type === "video") return <MediaVideo controls src={src}/>;
        if (type === "audio") return <audio controls src={src}/>;
        return (
            <DocCard href={src} target="_blank" rel="noopener noreferrer" download={mediaName || undefined}>
                <RiFileTextLine/> <span>{mediaName || t("words.download")}</span>
            </DocCard>
        );
    };

    const reactions = message.reactions || {};
    const reactionKeys = Object.keys(reactions).sort();
    const chips = reactionKeys.length ? (
        <ReactionRow data-testid="reaction-chips">
            {reactionKeys.map((emoji) => (
                <ReactionChip key={emoji}>{emoji} {reactions[emoji]}</ReactionChip>
            ))}
        </ReactionRow>
    ) : null;

    const body = (
        <>
            {media()}
            {content ? <Content>{content}</Content> : null}
            {chips}
            <ImageLightbox src={src} open={lightbox} onClose={() => setLightbox(false)}/>
        </>
    );

    if (fromMe) {
        return (
            <Row $me>
                <Bubble $me>
                    {body}
                    <Time $me>{formatHM(sentAt)}</Time>
                    <MenuTrigger ref={triggerRef} $me onClick={openMenu} aria-label="menu"><RiArrowDownSLine/></MenuTrigger>
                </Bubble>
                {menu}
            </Row>
        );
    }

    return (
        <Row>
            <Avatar $color={avatarColorFor(from?.jid || senderLabel)}>{initials(senderLabel)}</Avatar>
            <Bubble>
                <Sender data-testid="bubble-sender">{senderLabel}</Sender>
                {body}
                <Time>{formatHM(sentAt)}</Time>
                <MenuTrigger ref={triggerRef} onClick={openMenu} aria-label="menu"><RiArrowDownSLine/></MenuTrigger>
            </Bubble>
            {menu}
        </Row>
    );
};

GroupMessageBubble.propTypes = {
    message: PropTypes.object.isRequired,
    onPin: PropTypes.func,
};

export default GroupMessageBubble;
